'use client';

import { useEffect, useRef } from 'react';
import { useRouter } from 'next/navigation';

/**
 * Smart prefetching component that prefetches inner pages based on viewport visibility
 * This component uses Intersection Observer to prefetch links when they're about to come into view
 */
export default function SmartPrefetchInner() {
  const router = useRouter();
  const prefetchedUrls = useRef(new Set<string>());

  useEffect(() => {
    // Only run on client side
    if (typeof window === 'undefined') return;

    // Create an intersection observer to watch for links coming into viewport
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const link = entry.target as HTMLAnchorElement;
            const href = link.getAttribute('href');
            
            // Only prefetch internal links that haven't been prefetched yet
            if (href && href.startsWith('/') && !prefetchedUrls.current.has(href)) {
              // Prefetch news and makers detail pages
              if (href.includes('/news/') || href.includes('/makers/')) {
                prefetchedUrls.current.add(href);
                router.prefetch(href);
              }
            }
          }
        });
      },
      {
        // Start prefetching when link is 200px away from viewport
        rootMargin: '200px',
        threshold: 0
      }
    );

    // Observe all links to news and makers detail pages
    const observeLinks = () => {
      const links = document.querySelectorAll('a[href^="/news/"], a[href^="/makers/"]');
      links.forEach(link => observer.observe(link));
    };

    // Initial observation
    observeLinks();

    // Re-observe when DOM changes (for dynamically loaded content)
    const mutationObserver = new MutationObserver(() => {
      observeLinks();
    });

    mutationObserver.observe(document.body, {
      childList: true,
      subtree: true
    });

    // Cleanup
    return () => {
      observer.disconnect();
      mutationObserver.disconnect();
    };
  }, [router]);

  // Also prefetch based on hover with a slight delay
  useEffect(() => {
    let hoverTimeout: NodeJS.Timeout;

    const handleHover = (e: MouseEvent) => {
      const target = (e.target as HTMLElement).closest('a');
      if (!target) return;

      const href = target.getAttribute('href');
      if (href && href.startsWith('/') && !prefetchedUrls.current.has(href)) {
        if (href.includes('/news/') || href.includes('/makers/')) {
          // Prefetch after 100ms of hovering
          hoverTimeout = setTimeout(() => {
            prefetchedUrls.current.add(href);
            router.prefetch(href);
          }, 100);
        }
      }
    };

    const handleHoverEnd = () => {
      clearTimeout(hoverTimeout);
    };

    // Add hover listeners
    document.addEventListener('mouseover', handleHover);
    document.addEventListener('mouseout', handleHoverEnd);

    return () => {
      document.removeEventListener('mouseover', handleHover);
      document.removeEventListener('mouseout', handleHoverEnd);
      clearTimeout(hoverTimeout);
    };
  }, [router]);

  return null; // This component doesn't render anything
}